import { BaseService, ServiceResponse } from './base.service';
import { AchievementTrackingService } from './achievement-tracking.service';
import { PublicLeaderboardService } from './public-leaderboard.service';
import { StarredCoursesService } from './starred-courses.service';
import { PerformanceRecordsService } from './performance-records.service';
import { CourseLeaderboardService } from './course-leaderboard.service';
import { LocalLegendService } from './local-legend.service';
import type { 
  PerformanceZonesSummary,
  CourseDiscoveryItem,
  HoleRecord,
  CourseRecord,
  LocalLegendStatus,
  PerformanceRanking
} from '../../types/performance-zones';

/**
 * Unified Performance Zones Service
 * 
 * Consolidates multiple API services into a single coordinated interface
 * for all Performance Zones functionality. Provides:
 * 
 * - Centralized error handling and response standardization
 * - Coordinated data fetching with proper fallbacks
 * - Reduced service complexity and interdependencies
 * - Consistent response formatting
 * - Single point of access for all Performance Zones data
 */
export class PerformanceZonesService extends BaseService {
  private achievementTracking: AchievementTrackingService;
  private publicLeaderboard: PublicLeaderboardService;
  private starredCourses: StarredCoursesService;
  private performanceRecords: PerformanceRecordsService;
  private courseLeaderboard: CourseLeaderboardService;
  private localLegend: LocalLegendService;

  constructor() {
    super();
    this.achievementTracking = new AchievementTrackingService();
    this.publicLeaderboard = new PublicLeaderboardService();
    this.starredCourses = new StarredCoursesService();
    this.performanceRecords = new PerformanceRecordsService();
    this.courseLeaderboard = new CourseLeaderboardService();
    this.localLegend = new LocalLegendService();
  }

  /**
   * Get complete Performance Zones data for a user
   * Coordinates multiple services to provide comprehensive performance data
   */
  async getUserPerformanceZones(userId: string): Promise<ServiceResponse<PerformanceZonesSummary>> {
    return this.withErrorHandling(async () => {
      const result = await this.achievementTracking.getUserPerformanceZones(userId);
      return result.data;
    });
  }

  /**
   * Get course discovery data
   * Fetches all available courses with leaderboard information
   */
  async getCourseDiscovery(userId?: string): Promise<ServiceResponse<CourseDiscoveryItem[]>> {
    return this.withErrorHandling(async () => {
      const result = await this.publicLeaderboard.getCourseDiscovery(userId);
      return result.data;
    });
  }

  /**
   * Get starred courses for a user
   */
  async getStarredCourses(userId: string): Promise<ServiceResponse<string[]>> {
    return this.withErrorHandling(async () => {
      const result = await this.starredCourses.getStarredCourses(userId);
      return result.data;
    });
  }

  /**
   * Toggle starred course status
   */
  async toggleStarredCourse(userId: string, courseId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const result = await this.starredCourses.toggleStarredCourse(userId, courseId);
      return result.data;
    });
  }

  /**
   * Refresh all Performance Zones data
   * Fetches fresh data from the achievement tracking service
   */
  async refreshPerformanceZones(userId: string): Promise<ServiceResponse<PerformanceZonesSummary>> {
    return this.withErrorHandling(async () => {
      return this.getUserPerformanceZones(userId);
    });
  }

  /**
   * Get comprehensive Performance Zones data for initial load
   * Coordinates multiple API calls with proper error handling and graceful fallbacks
   */
  async getInitialPerformanceZonesData(userId: string): Promise<ServiceResponse<{
    performanceData: PerformanceZonesSummary;
    courseDiscovery: CourseDiscoveryItem[];
    starredCourses: string[];
  }>> {
    return this.withErrorHandling(async () => {
      // Fetch all data in parallel for better performance
      const [performanceResult, courseResult, starredResult] = await Promise.allSettled([
        this.achievementTracking.getUserPerformanceZones(userId),
        this.publicLeaderboard.getCourseDiscovery(userId),
        this.starredCourses.getStarredCourses(userId)
      ]);

      // Check if ALL calls failed - throw error to stop infinite spinner
      const allFailed = 
        (performanceResult.status === 'rejected' || !performanceResult.value?.success) &&
        (courseResult.status === 'rejected' || !courseResult.value?.success) &&
        (starredResult.status === 'rejected' || !starredResult.value?.success);

      if (allFailed) {
        // Throw error so withErrorHandling catches it and shows error message
        const errorMsg = performanceResult.status === 'rejected' 
          ? performanceResult.reason?.message 
          : performanceResult.value?.error;
        throw new Error(errorMsg || 'Failed to load Performance Zones data. Please check your connection.');
      }

      // Handle results with graceful fallbacks (at least one call succeeded)
      const performanceData = performanceResult.status === 'fulfilled' && performanceResult.value.success 
        ? performanceResult.value.data 
        : null;

      const courseDiscovery = courseResult.status === 'fulfilled' && courseResult.value.success
        ? courseResult.value.data
        : [];

      const starredCourses = starredResult.status === 'fulfilled' && starredResult.value.success
        ? starredResult.value.data
        : [];

      // Return data even if some calls failed - graceful degradation
      return {
        performanceData: performanceData || {
          hole_records: [],
          course_records: [],
          local_legends: [],
          top10_holes: [],
          top10_courses: [],
          achievements: [],
          progress_updates: [],
          record_notifications: []
        },
        courseDiscovery: courseDiscovery || [],
        starredCourses: starredCourses || []
      };
    });
  }

  /**
   * Get performance records with unified error handling
   */
  async getPerformanceRecords(userId: string): Promise<ServiceResponse<{
    holeRecords: HoleRecord[];
    courseRecords: CourseRecord[];
  }>> {
    return this.withErrorHandling(async () => {
      // This would coordinate with the PerformanceRecordsService
      // For now, we get this data from the main performance zones call
      const result = await this.getUserPerformanceZones(userId);
      
      if (result.success && result.data) {
        return {
          holeRecords: result.data.hole_records || [],
          courseRecords: result.data.course_records || []
        };
      }

      return {
        holeRecords: [],
        courseRecords: []
      };
    });
  }

  /**
   * Get local legends with unified error handling
   */
  async getLocalLegends(userId: string): Promise<ServiceResponse<LocalLegendStatus[]>> {
    return this.withErrorHandling(async () => {
      const result = await this.getUserPerformanceZones(userId);
      
      if (result.success && result.data) {
        return result.data.local_legends || [];
      }

      return [];
    });
  }

  /**
   * Get top 10 rankings with unified error handling
   */
  async getTop10Rankings(userId: string): Promise<ServiceResponse<{
    top10Holes: PerformanceRanking[];
    top10Courses: PerformanceRanking[];
  }>> {
    return this.withErrorHandling(async () => {
      const result = await this.getUserPerformanceZones(userId);
      
      if (result.success && result.data) {
        return {
          top10Holes: result.data.top10_holes || [],
          top10Courses: result.data.top10_courses || []
        };
      }

      return {
        top10Holes: [],
        top10Courses: []
      };
    });
  }
}